/**
 * Referral cookie helpers — mirrors lib/attribution/cookie.ts but holds just
 * the referral code an affiliate's URL planted. Set on first visit with
 * `?ref=CODE`; read once at signup completion, then cleared after
 * `/api/affiliate/track-referral` succeeds.
 *
 * Domain handling mirrors the attribution cookie so links from the marketing
 * apex carry through to the app subdomain.
 */

export const REFERRAL_COOKIE_NAME = "msp_referral";
export const REFERRAL_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

function getCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname;
  if (host === "marketsniper.pro" || host.endsWith(".marketsniper.pro")) {
    return ".marketsniper.pro";
  }
  return undefined;
}

function isValidCode(code: string): boolean {
  return /^[A-Za-z0-9_-]{3,32}$/.test(code);
}

export function readReferralCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(/;\s*/)
    .find((c) => c.startsWith(`${REFERRAL_COOKIE_NAME}=`));
  if (!match) return null;
  const raw = decodeURIComponent(match.slice(REFERRAL_COOKIE_NAME.length + 1));
  return isValidCode(raw) ? raw.toUpperCase() : null;
}

export function writeReferralCookie(code: string, force = false): boolean {
  if (typeof document === "undefined") return false;
  if (!isValidCode(code)) return false;
  if (!force && readReferralCookie()) return false;

  const domain = getCookieDomain();
  const parts = [
    `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(code.toUpperCase())}`,
    `Max-Age=${REFERRAL_COOKIE_MAX_AGE}`,
    "Path=/",
    "SameSite=Lax",
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    parts.push("Secure");
  }
  document.cookie = parts.join("; ");
  return true;
}

export function clearReferralCookie(): void {
  if (typeof document === "undefined") return;
  const domain = getCookieDomain();
  const parts = [`${REFERRAL_COOKIE_NAME}=`, "Max-Age=0", "Path=/"];
  if (domain) parts.push(`Domain=${domain}`);
  document.cookie = parts.join("; ");
}

export function detectReferralFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const code = new URLSearchParams(window.location.search).get("ref");
  if (!code) return null;
  return isValidCode(code) ? code.toUpperCase() : null;
}
